import { NetworkService } from '@agroone-front/shared'
import { PesticideRules } from '@agroone/business'
import { PesticideProductLite } from '@agroone/entities'
import { inject, Injectable } from '@angular/core'
import { environment } from '@front-app-environments/environment'
import { catchError, from, Observable, shareReplay, throwError } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class PesticideService {
  private networkService = inject(NetworkService)

  /**
   * The pesticides list is reference data that is stable during a session. The
   * intervention wizard re-runs its resolver on every step change, so cache the
   * result per query to avoid re-fetching the (heavy) `/lite` endpoint each time.
   */
  private cache = new Map<string, Observable<PesticideProductLite[]>>()

  /**
   * Drop the cached pesticides so the next request hits the network again.
   * Called when the app refreshes its data (see IdbStorageService.clearStorage).
   */
  public clearCache(): void {
    this.cache.clear()
  }

  /** @param lastUse Last Use Date cutoff; see PesticideRules for the RG1 defaults. */
  public getAll(
    cropType?: string,
    country?: string | null,
    lastUse: string | null | undefined = PesticideRules.INTERVENTION_LAST_USE_CUTOFF,
    sorted?: boolean,
  ): Observable<PesticideProductLite[]> {
    const cacheKey = `${cropType ?? ''}|${country ?? ''}|${lastUse ?? ''}|${sorted ?? ''}`
    const cached = this.cache.get(cacheKey)
    if (cached) {
      return cached
    }

    const options: {
      params: { [key: string]: string | number | boolean }
    } = {
      params: {},
    }

    if (cropType) {
      options.params = { ...options.params, cropType }
    }
    if (country) {
      options.params = { ...options.params, country }
    }
    if (lastUse) {
      options.params = { ...options.params, lastUse }
    }
    if (sorted) {
      options.params = { ...options.params, sorted }
    }

    options.params = { ...options.params, ...{ pageLength: 1000 } }

    const request$ = from(
      this.networkService.getAllFromPaginated<PesticideProductLite>(
        `${environment.apiUrl}${environment.pesticides}/lite`,
        {
          params: options.params,
        },
      ),
    ).pipe(
      catchError((err) => {
        // Do not cache a failed request, so a later navigation can retry.
        this.cache.delete(cacheKey)
        return throwError(() => err)
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    )

    this.cache.set(cacheKey, request$)

    return request$
  }
}
